import * as React from "react";
import { IDropdownOption } from "@fluentui/react";
import {
    Combobox,
    Option,
    SelectionEvents,
    OptionOnSelectData,
    useId
} from "@microsoft/shifts-ui-core";

const styles = require("./SlotSelector.scss");

export interface SlotSelectorProps {
    /** The available slot options */
    options: IDropdownOption[];
    /** The currently selected number of slots */
    selectedSlots: number;
    /** Callback when the selection changes */
    onSelectionChange: (selectedValue: number) => void;
    /** Aria label for accessibility */
    ariaLabel: string;
    /** Data automation ID for testing */
    automationId?: string;
    /** Additional CSS class name */
    className?: string;
    /** Appearance style of the select component */
    appearance?: "filled-darker" | "outline" | "underline";
    /** Tab index for keyboard navigation */
    tabIndex?: number;
}

/**
 * SlotSelector component using Fluent UI Combobox with filtering
 * Replaces the previous Dropdown implementation for slot selection
 */
export const SlotSelector: React.FC<SlotSelectorProps> = ({
    options,
    selectedSlots,
    onSelectionChange,
    ariaLabel,
    automationId,
    className,
    appearance,
    tabIndex
}) => {
    const selectId = useId();
    const [comboboxInputValue, setComboboxInputValue] = React.useState<string>("");
    const [isUserTyping, setIsUserTyping] = React.useState<boolean>(false);

    const handleSelectionChange = (event: SelectionEvents, data: OptionOnSelectData) => {
        const selectedValue = Number(data.optionValue);
        if (onSelectionChange && !isNaN(selectedValue)) {
            onSelectionChange(selectedValue);
            setComboboxInputValue("");
            setIsUserTyping(false);
        }
    };

    // Get the display text for the selected slot value
    const getSelectedDisplayText = (): string => {
        const selectedOption = options.find(opt => Number(opt.key) === selectedSlots);
        return selectedOption ? selectedOption.text : selectedSlots.toString();
    };

    return (
        <>
            <label
                htmlFor={selectId}
                className={styles.srOnlyLabel}>
                {ariaLabel}
            </label>
            <Combobox
                tabIndex={tabIndex}
                appearance={appearance}
                id={selectId}
                value={isUserTyping ? comboboxInputValue : getSelectedDisplayText()}
                onOptionSelect={handleSelectionChange}
                onChange={(e) => {
                    const newValue = (e.target as HTMLInputElement).value;
                    setComboboxInputValue(newValue);
                    setIsUserTyping(true);
                }}
                aria-label={ariaLabel}
                data-automation-id={automationId}
                className={className}
                freeform>
                {options
                    .filter(option => {
                        // If user is typing, filter options based on input
                        if (isUserTyping && comboboxInputValue) {
                            return option.text.toLowerCase().includes(comboboxInputValue.toLowerCase());
                        }
                        // Otherwise show all options
                        return true;
                    })
                    .map((option) => (
                        <Option
                            id={`QAIDOpenShiftSlots-slot-${option.key}`}
                            key={option.key}
                            aria-label={option.text}
                            value={option.key.toString()}>
                            {option.text}
                        </Option>
                    ))}
            </Combobox>
        </>
    );
};
